import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import type { EmailCampaign, EmailTemplate } from "../../../types/marketing";
import { AudienceSelector } from "./AudienceSelector";
import { CampaignReview } from "./CampaignReview";
import { EmailPreview } from "./EmailPreview";
import {
  allEmailRows,
  campaignPayload,
  emailError,
  normalizeCampaign,
  timezoneName,
} from "./emailWorkflows";
export function CampaignComposer({
  initial,
  onClose,
}: {
  initial: Partial<EmailCampaign>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(() => normalizeCampaign(initial));
  const [baseline, setBaseline] = useState(() =>
    JSON.stringify(normalizeCampaign(initial)),
  );
  const [step, setStep] = useState(0);
  const [delivery, setDelivery] = useState<"draft" | "send-now" | "schedule">(
    "draft",
  );
  const [schedule, setSchedule] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const cache = useQueryClient();
  const templates = useQuery({
    queryKey: ["email-templates", "active-options"],
    queryFn: () =>
      allEmailRows<EmailTemplate>(
        "/api/marketing/email-templates/?archived=false",
      ),
  });
  const template = templates.data?.find((t) => t.id === draft.template);
  const change = (patch: Partial<EmailCampaign>) =>
    setDraft((previous) => ({ ...previous, ...patch }));
  const close = () => {
    if (
      !busy &&
      (JSON.stringify(draft) === baseline ||
        window.confirm("Discard unsaved campaign changes?"))
    )
      onClose();
  };
  const save = async (review: boolean) => {
    setBusy(true);
    setError("");
    try {
      const result = draft.id
        ? await api.updateEmailCampaign(draft.id, campaignPayload(draft))
        : await api.createEmailCampaign(campaignPayload(draft));
      const saved = normalizeCampaign(result.data);
      setDraft(saved);
      setBaseline(JSON.stringify(saved));
      await cache.invalidateQueries({ queryKey: ["email-campaigns"] });
      await cache.invalidateQueries({ queryKey: ["email-review", saved.id] });
      if (review) setStep(3);
      else onClose();
    } catch (cause) {
      setError(emailError(cause));
    }
    setBusy(false);
  };
  const ready = Boolean(
    draft.name?.trim() &&
      (draft.html_content?.trim() || template?.html_content) &&
      (draft.subject?.trim() || template?.subject),
  );
  return (
    <Modal open onClose={close}>
      <ModalDialog
        sx={{
          width: 940,
          maxWidth: "96vw",
          maxHeight: "94vh",
          overflowY: "auto",
        }}
      >
        <Typography level="h4">
          {draft.id ? "Edit campaign" : "New campaign"}
        </Typography>
        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
          {["Content", "Audience", "Delivery", "Review"].map((label, index) => (
            <Typography
              key={label}
              level="title-sm"
              color={index === step ? "primary" : "neutral"}
            >
              {index + 1}. {label}
            </Typography>
          ))}
        </Stack>
        {error && <Alert color="danger">{error}</Alert>}
        {step === 0 && (
          <Stack spacing={2}>
            <FormControl>
              <FormLabel>Campaign name</FormLabel>
              <Input
                value={draft.name || ""}
                onChange={(event) => change({ name: event.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Active template</FormLabel>
              <Select
                value={draft.template || null}
                onChange={(_, value) => change({ template: value })}
              >
                {templates.data?.map((t) => (
                  <Option key={t.id} value={t.id}>
                    {t.name}
                  </Option>
                ))}
              </Select>
            </FormControl>
            {templates.isError && (
              <Alert
                color="danger"
                endDecorator={
                  <Button onClick={() => templates.refetch()}>Retry</Button>
                }
              >
                Templates unavailable.
              </Alert>
            )}
            <FormControl>
              <FormLabel>Subject override</FormLabel>
              <Input
                value={draft.subject || ""}
                placeholder={template?.subject || "Use the template subject"}
                onChange={(event) => change({ subject: event.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Inbox preview override</FormLabel>
              <Input
                value={draft.preview_text || ""}
                placeholder={
                  template?.preview_text || "Use the template preview text"
                }
                onChange={(event) =>
                  change({ preview_text: event.target.value })
                }
              />
            </FormControl>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1,
              }}
            >
              <FormControl>
                <FormLabel>From name</FormLabel>
                <Input
                  value={draft.from_name || ""}
                  onChange={(event) =>
                    change({ from_name: event.target.value })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Reply-to email</FormLabel>
                <Input
                  type="email"
                  value={draft.reply_to || ""}
                  onChange={(event) => change({ reply_to: event.target.value })}
                />
              </FormControl>
            </Box>
            {draft.html_content && (
              <Alert
                color="neutral"
                endDecorator={
                  <Button onClick={() => change({ html_content: "" })}>
                    Use template body
                  </Button>
                }
              >
                This draft has custom HTML overriding its template.
              </Alert>
            )}
            <EmailPreview
              html={draft.html_content || template?.html_content || ""}
              subject={draft.subject || template?.subject}
              preheader={draft.preview_text || template?.preview_text}
            />
          </Stack>
        )}
        {step === 1 && <AudienceSelector draft={draft} onChange={change} />}
        {step === 2 && (
          <Stack spacing={2}>
            <FormControl>
              <FormLabel>Delivery choice</FormLabel>
              <Select
                value={delivery}
                onChange={(_, value) => setDelivery(value || "draft")}
              >
                <Option value="draft">Keep as draft</Option>
                <Option value="send-now">Send after final review</Option>
                <Option value="schedule">Schedule after final review</Option>
              </Select>
            </FormControl>
            {delivery === "schedule" && (
              <FormControl>
                <FormLabel>Send at ({timezoneName})</FormLabel>
                <Input
                  type="datetime-local"
                  value={schedule}
                  onChange={(event) => setSchedule(event.target.value)}
                />
              </FormControl>
            )}
            <Alert color="neutral">
              The next step saves your draft and resolves the audience. Email
              delivery requires a separate confirmation on the review step.
            </Alert>
          </Stack>
        )}
        {step === 3 && draft.id && (
          <CampaignReview
            id={draft.id}
            action={delivery}
            draftLabel="Keep as draft"
            scheduledAt={schedule}
            onComplete={onClose}
            onBack={() => setStep(2)}
          />
        )}
        {step < 3 && (
          <Stack
            direction="row"
            useFlexGap
            flexWrap="wrap"
            spacing={1}
            justifyContent="flex-end"
          >
            <Button variant="plain" disabled={busy} onClick={close}>
              Cancel
            </Button>
            {step > 0 && (
              <Button
                variant="plain"
                onClick={() => setStep(step - 1)}
                disabled={busy}
              >
                Back
              </Button>
            )}
            <Button
              variant="outlined"
              disabled={!draft.name?.trim()}
              loading={busy}
              onClick={() => save(false)}
            >
              Save draft
            </Button>
            <Button
              loading={busy}
              disabled={
                !ready ||
                (step === 2 &&
                  delivery === "schedule" &&
                  (!schedule ||
                    Number.isNaN(new Date(schedule).valueOf()) ||
                    new Date(schedule) <= new Date()))
              }
              onClick={() => (step === 2 ? save(true) : setStep(step + 1))}
            >
              {step === 2 ? "Save and review" : "Next"}
            </Button>
          </Stack>
        )}
      </ModalDialog>
    </Modal>
  );
}
